import { ShoppingBag, Globe2, Check, ArrowRight, MessageCircle, MapPin, Phone, User, Shirt, ShieldCheck, Users, Target, Droplets, Award, Palette, Tag } from "lucide-react";
import { CONTACT } from "@/lib/i18n";

const ICONS = [ShoppingBag, Globe2];

const formatCurrency = (value) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const QuoteCard = ({ item, waHref, onOpenConsult, idx }) => {
  return (
    <article
      data-testid={`commerce-card-${idx}`}
      className="relative bg-[#0a192f] border border-white/10 hover:border-[#d4af37] transition-all duration-300 p-8 lg:p-10 flex flex-col group lg:col-span-2"
    >
      {/* Tag */}
      <div className="absolute top-0 right-0 bg-[#d4af37] text-[#0a192f] px-4 py-1.5 font-display font-black text-xs tracking-wider">
        {item.tag}
      </div>

      <div className="w-14 h-14 flex items-center justify-center border-2 border-[#d4af37]/40 mb-6">
        <ShoppingBag className="w-6 h-6 text-[#d4af37]" strokeWidth={1.5} />
      </div>

      <h3 className="font-display text-2xl sm:text-3xl font-bold leading-snug">
        {item.title}
      </h3>
      <p className="mt-3 text-sm text-white/70 leading-relaxed">{item.desc}</p>

      {/* Quote Header */}
      <div className="mt-6 bg-[#d4af37]/10 border border-[#d4af37]/20 px-4 py-3 rounded">
        <p className="text-xs font-display font-bold text-[#d4af37] tracking-wide">
          {item.quoteHeader}
        </p>
      </div>

      {/* Image Placeholder area instead of table */}
      <div className="mt-8 relative w-full flex flex-col items-center">
        <div className="w-full rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-black/20">
          <img 
            src={item.imagePlaceholder || "/epis.jpg"} 
            alt={item.title} 
            className="w-full h-auto object-contain"
          />
        </div>
      </div>

      {/* Location */}
      <div className="mt-6 flex items-center gap-2 text-white/60 text-sm">
        <MapPin className="w-4 h-4 text-[#d4af37]" />
        <span>{item.location}</span>
      </div>

      {/* Commercial Conditions */}
      <div className="mt-5">
        <p className="text-xs font-display font-bold text-white/50 uppercase tracking-wider mb-3">Condições Comerciais</p>
        <ul className="space-y-2">
          {item.conditions.map((c, j) => (
            <li key={j} className="flex gap-2 items-start text-sm text-white/75">
              <Check className="w-4 h-4 text-[#d4af37] mt-0.5 flex-shrink-0" strokeWidth={3} />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Contact */}
      <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/70 bg-white/[0.03] border border-white/5 px-4 py-3 rounded">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-[#d4af37]" />
          <span className="font-medium text-white/90">{item.contactName}</span>
        </div>
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-[#d4af37]" />
          <span>{item.contactPhone} (WhatsApp)</span>
        </div>
      </div>

      {/* CTAs */}
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button
          onClick={onOpenConsult}
          data-testid={`commerce-cta-form-${idx}`}
          className="btn-primary text-xs flex-1"
        >
          {item.cta}
          <ArrowRight className="w-4 h-4" />
        </button>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`commerce-cta-wa-${idx}`}
          className="btn-secondary text-xs flex-1"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </a>
      </div>
    </article>
  );
};

const BulletCard = ({ item, Icon, waHref, onOpenConsult, idx }) => (
  <article
    data-testid={`commerce-card-${idx}`}
    className="relative bg-[#0a192f] border border-white/10 hover:border-[#d4af37] transition-all duration-300 p-8 lg:p-10 flex flex-col group"
  >
    <div className="absolute top-0 right-0 bg-[#d4af37] text-[#0a192f] px-4 py-1.5 font-display font-black text-xs tracking-wider">
      {item.tag}
    </div>

    <div className="w-14 h-14 flex items-center justify-center border-2 border-[#d4af37]/40 mb-6">
      <Icon className="w-6 h-6 text-[#d4af37]" strokeWidth={1.5} />
    </div>

    <h3 className="font-display text-2xl sm:text-3xl font-bold leading-snug">
      {item.title}
    </h3>
    <p className="mt-3 text-sm text-white/70 leading-relaxed">{item.desc}</p>

    <ul className="mt-6 space-y-2.5">
      {item.bullets.map((b, j) => (
        <li key={j} className="flex gap-2 items-start text-sm text-white/85">
          <Check className="w-4 h-4 text-[#d4af37] mt-0.5 flex-shrink-0" strokeWidth={3} />
          <span>{b}</span>
        </li>
      ))}
    </ul>

    <div className="mt-8 flex flex-col sm:flex-row gap-3">
      <button
        onClick={onOpenConsult}
        data-testid={`commerce-cta-form-${idx}`}
        className="btn-primary text-xs flex-1"
      >
        {item.cta}
        <ArrowRight className="w-4 h-4" />
      </button>
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`commerce-cta-wa-${idx}`}
        className="btn-secondary text-xs flex-1"
      >
        <MessageCircle className="w-4 h-4" />
        WhatsApp
      </a>
    </div>
  </article>
);

const Commerce = ({ t, onOpenConsult }) => {
  const waMessages = {
    pt: "Olá BID! Tenho interesse na divisão comercial (EPIs / Importação e Exportação).",
    en: "Hello BID! I'm interested in your commercial division (PPE / Import & Export).",
    fr: "Bonjour BID ! Je suis intéressé(e) par votre division commerciale (EPI / Import & Export).",
  };
  const langKey = t?.hero?.cta === "Book Technical Consulting" ? "en" : t?.hero?.cta === "Planifier une consultation" ? "fr" : "pt";
  const waMessage = encodeURIComponent(waMessages[langKey] || waMessages.pt);
  const waHref = `https://wa.me/${CONTACT.whatsappRaw}?text=${waMessage}`;

  return (
    <section
      id="commerce"
      data-testid="commerce-section"
      className="relative bg-[#060d18] text-white py-24 sm:py-32 overflow-hidden"
    >
      {/* Decorative pattern */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, #d4af37 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative bid-container">
        <div className="max-w-3xl mb-14">
          <p className="bid-overline text-[#d4af37] mb-4">{t.commerce.overline}</p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tight leading-tight"
            data-testid="commerce-title"
          >
            {t.commerce.title}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-white/70 leading-relaxed">
            {t.commerce.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {t.commerce.items.map((item, i) => {
            if (item.isQuote) {
              return (
                <QuoteCard
                  key={i}
                  item={item}
                  waHref={waHref}
                  onOpenConsult={onOpenConsult}
                  idx={i}
                />
              );
            }
            const Icon = ICONS[i] || ShoppingBag;
            return (
              <BulletCard
                key={i}
                item={item}
                Icon={Icon}
                waHref={waHref}
                onOpenConsult={onOpenConsult}
                idx={i}
              />
            );
          })}
        </div>

        {/* Uniformes Section */}
        {t.commerce.uniforms && (
          <div className="mt-24 pt-16 border-t border-white/10" data-testid="commerce-uniforms">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              
              {/* Left Column: Info */}
              <div>
                <div className="bg-[#d4af37] text-[#0a192f] inline-block px-4 py-1.5 font-display font-black text-xs tracking-wider mb-6">
                  {t.commerce.uniforms.tag}
                </div>
                
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
                  {t.commerce.uniforms.title}
                </h3>
                <p className="text-lg text-white/80 mb-10 leading-relaxed font-light">
                  {t.commerce.uniforms.subtitle}
                </p>

                {/* Characteristics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                  {t.commerce.uniforms.characteristics.map((char, index) => {
                    const icons = [Shirt, Target, ShieldCheck, Users];
                    const Icon = icons[index % icons.length];
                    return (
                      <div key={index} className="flex gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-[#d4af37]" />
                        </div>
                        <div>
                          <h4 className="text-white font-bold mb-1 text-sm">{char.title}</h4>
                          <p className="text-white/60 text-xs leading-relaxed">{char.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <button
                  onClick={onOpenConsult}
                  className="btn-primary"
                >
                  {t.commerce.uniforms.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Right Column: Visuals & Segments */}
              <div className="relative flex flex-col items-center">
                <div className="w-full rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-black/20">
                  <img 
                    src={t.commerce.uniforms.imagePlaceholder} 
                    alt="Uniformes Personalizados" 
                    className="w-full h-auto object-contain"
                  />
                </div>
                
                {/* Segments moved below the image to not block it */}
                <div className="mt-8 w-full">
                  <p className="text-[#d4af37] font-display font-bold text-xs uppercase tracking-wider mb-4 text-center">Atendimento para diversos segmentos</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {t.commerce.uniforms.segments.map((segment, idx) => (
                      <span key={idx} className="bg-white/5 border border-white/10 text-white/90 text-xs px-4 py-2 rounded-full transition-colors hover:bg-white/10 hover:border-[#d4af37]/50">
                        {segment}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Features Ribbon */}
            <div className="mt-12 bg-[#0a192f]/50 border border-white/10 rounded-xl p-6 sm:p-8">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {t.commerce.uniforms.features.map((feature, idx) => {
                  const featureIcons = [Target, Droplets, Award, Palette, Tag];
                  const Icon = featureIcons[idx % featureIcons.length];
                  return (
                    <div key={idx} className="flex flex-col items-center text-center group">
                       <Icon className="w-6 h-6 text-[#d4af37] mb-3 group-hover:scale-110 transition-transform" />
                       <h5 className="text-white text-sm font-bold mb-1">{feature.title}</h5>
                       <p className="text-white/50 text-xs">{feature.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Commerce;
